import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Entry from 'invite-sign/models/entry';
import type Flow from 'invite-sign/models/flow';
import type VisitorSearchService from 'invite-sign/services/visitor-search';

export type VisitorStep = {
  /**
   * A "Hidden" step is one that should have zero effect on the UI, but it used purely for internal state
   * (e.g., for navigating back/forward through steps), without appearing in the list of steps or affecting
   * the displayed number or count of steps.
   */
  hidden?: boolean;
  label: string;
  /**
   * If true, the route requires a userDocumentTemplate ID as a route param.
   */
  needsDocId?: boolean;
  route: string;
  /**
   * Should this step be skipped when moving backwards?
   * Sometimes a route (e.g., 'visitor.visitor-type') may only change some state and
   * then transition to the next step when moving forward. When moving backward, we
   * need to skip this step in order avoid that behavior (and thus ending up right back
   * where we started).
   */
  skipWhenMovingBackwards?: boolean;
  /**
   * Treat this step as though it was a substate of another route.
   */
  substateOf?: string;
};

export default class VisitorStepsService extends Service {
  @service declare router: RouterService;
  @service declare visitorSearch: VisitorSearchService;

  @tracked availableFlows: Flow[] = [];
  @tracked entry: Entry | null = null;
  @tracked selectedFlow: Flow | null = null;

  @tracked backwards = false;
  @tracked currentStepIndex = 0;

  @tracked skipAgreementStepWhenMovingBackwards = false;
  @tracked skipDocumentStepWhenMovingBackwards = false;

  get currentStep(): VisitorStep | undefined {
    return this.steps[this.currentStepIndex];
  }

  get hasAgreementStep(): boolean {
    return this.selectedFlow?.agreementPage?.enabled ?? false;
  }

  get hasDocumentStep(): boolean {
    if (!this.selectedFlow) return false;
    // return this.entry.incompleteUserDocumentTemplates.length > 0;
    return this.selectedFlow.userDocumentTemplates.length > 0;
  }

  get hasIdScanStep(): boolean {
    // Skip only on explicit confirmation: `matchConfirmed` defaults true and would also skip when
    // verify-match was never shown (no email/phone), silently bypassing identity verification.
    const skipForReturningVisitor =
      this.visitorSearch.matchExplicitlyConfirmed && this.visitorSearch.searchedReturningVisitor?.skipIdScan;
    return (this.selectedFlow?.idScanPage?.enabled ?? false) && !skipForReturningVisitor;
  }

  get hasNextStep(): boolean {
    if (!this.nextStep) return false;
    if (this.currentStepIndex + 1 === this.steps.length - 1) return false;
    return true;
  }

  get hasPhotoStep(): boolean {
    if (!this.selectedFlow) return false;
    return this.selectedFlow.photoPage?.enabled ?? false;
  }

  get hasVerifyMatchStep(): boolean {
    const { searchedInvite, searchedReturningVisitor, matchConfirmed } = this.visitorSearch;

    if (matchConfirmed) {
      const hasRelevantUserData = this.visitorSearch.hasRelevantUserData();

      if (searchedInvite && hasRelevantUserData) {
        return true;
      }

      if (searchedReturningVisitor && hasRelevantUserData) {
        return this.visitorSearch.shouldVerifyMatchForReturningVisitor(this.availableFlows);
      }
    }

    return false;
  }

  get lastStep(): VisitorStep | undefined {
    return this.steps.at(-1);
  }

  get nextStep(): VisitorStep | undefined {
    return this.steps[this.currentStepIndex + 1];
  }

  get previousStep(): VisitorStep | undefined {
    for (let i = this.currentStepIndex - 1; i >= 0; i--) {
      const step = this.steps[i];
      if (step?.skipWhenMovingBackwards) continue;
      return step;
    }
    return;
  }

  get steps(): VisitorStep[] {
    const steps: VisitorStep[] = [
      // always show "full name" step
      { label: 'steps.full_name', route: 'visitor.name' },
    ];

    // If the entered name matched an invite, or is a returning visitor, there's a "verify match" step
    // that is more like a sub-state of the "full name" step.
    if (this.hasVerifyMatchStep) {
      steps.push({ hidden: true, label: '', route: 'visitor.verify-match', substateOf: 'visitor.name' });
    }

    // If there is more than one Visitor Type, show the "Type of visit" step. (If we don't show it, skip
    // it when moving backwards through steps, because when there's only one flow the route sets that flow
    // on the new entry advances to the next step, which would undo the "back" action.)
    const hasOnlyOneFlow = this.availableFlows.length === 1;
    steps.push({
      hidden: hasOnlyOneFlow,
      label: 'steps.type_of_visit',
      route: 'visitor.visitor-type',
      skipWhenMovingBackwards: hasOnlyOneFlow || this.visitorSearch.matchConfirmed,
    });

    // If there is a selected flow, and it has "id scanning" enabled, show the "Scan your ID" step.
    // Skip if this is a returning visitor whose previous visit allows reusing their existing ID scan.
    if (this.hasIdScanStep) {
      steps.push({ label: 'steps.scan_your_id', route: 'visitor.id-scan' });
    }

    // always show "Personal details" step. If there is only one flow and it's an employee
    // flow, show the "Personal details" label but divert into the "employee registration" page
    if (this.availableFlows.length === 1 && this.availableFlows[0]!.employeeCentric) {
      steps.push({ label: 'steps.personal_details', route: 'visitor.employee-registration' });
    } else {
      steps.push({ label: 'steps.personal_details', route: 'visitor.personal-details' });
    }

    // Remaining steps are conditional based on the selected flow; if there's no selected flow,
    // or the selected flow is an employee one, don't add any more steps.
    if (this.selectedFlow && !this.selectedFlow.employeeCentric) {
      if (this.selectedFlow.flowQuestionnaires.length > 0) {
        const questionnairesStep = {
          label: 'steps.take_assessments',
          route: 'visitor.questionnaires',
        };
        steps.push(questionnairesStep);
      }

      if (this.hasAgreementStep) {
        steps.push({
          label: 'steps.documents',
          route: 'visitor.sign-documents',
          skipWhenMovingBackwards: this.skipAgreementStepWhenMovingBackwards,
        });
      }

      if (this.hasDocumentStep) {
        steps.push({
          hidden: this.hasAgreementStep, // hide this if there are agreements since they are considered the same step as agreements in the list
          label: 'steps.documents',
          needsDocId: true,
          route: 'visitor.upload-documents',
          skipWhenMovingBackwards: this.skipDocumentStepWhenMovingBackwards,
          substateOf: this.hasAgreementStep ? 'visitor.sign-documents' : undefined,
        });
      }

      // Show "Photo" step if specified for the flow
      if (this.hasPhotoStep) {
        steps.push({ label: 'steps.add_photo', route: 'visitor.add-photo' });
      }
    }

    steps.push({ hidden: true, label: '', route: 'visitor.submitted' });

    return steps;
  }

  goToNextStep({ after }: { after?: string } = {}): Transition | void {
    // `steps` is recomputed from tracked match state, so a step (e.g. the hidden
    // verify-match step for a matched returning visitor) can be inserted before the
    // current one after the index was set on route entry. Re-anchor to the active
    // route so a stale index can't make `nextStep` resolve to the current route — a
    // same-route no-op that silently stalls the wizard (SQ-7356).
    this.setCurrentStepFromRoute(this.router.currentRouteName ?? undefined);

    let nextStep = this.nextStep;
    if (after) {
      const stepIndex = this.steps.findIndex((step) => step.route === after);
      nextStep = this.steps[stepIndex + 1];
    }

    if (!nextStep) return;

    this.backwards = false;

    // If the step requires a user document template ID, use the first one as the 'model' param to the route
    if (nextStep.needsDocId) {
      const firstModel = this.selectedFlow?.userDocumentTemplates?.[0];
      if (firstModel) {
        return this.router.transitionTo(nextStep.route, firstModel.id);
      }
    }

    // Otherwise, simply transition to specified route
    return this.router.transitionTo(nextStep.route);
  }

  goToPreviousStep({ before }: { before?: string } = {}): void {
    let previousStep = this.previousStep;
    if (before) {
      const stepIndex = this.steps.findIndex((step) => step.route === before);
      if (stepIndex) {
        previousStep = this.steps[stepIndex - 1];
      }
    }

    if (previousStep) {
      this.backwards = true;
      // If the step requires a user document template ID, use the last one as the 'model' param to the route
      if (previousStep.needsDocId) {
        const lastModel = this.selectedFlow?.userDocumentTemplates?.at(-1);
        if (lastModel) {
          this.router.transitionTo(previousStep.route, lastModel.id);
          return;
        }
      }

      // Otherwise, simply transition to specified route
      this.router.transitionTo(previousStep.route);
    }
  }

  setCurrentStepFromRoute(routeName: string | undefined): void {
    if (!routeName) return;
    const stepIndex = this.steps.findIndex((step) => step.route === routeName);
    if (stepIndex !== -1) {
      this.currentStepIndex = stepIndex;
    }
  }
}
